import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, CheckCircle2, ChevronDown, HardHat, Building2, MapPin } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import Seo from '@/components/Seo';
import TutorialPlayer from '@/components/tutorial/TutorialPlayer';
import AnimatedExamples from '@/components/tutorial/AnimatedExamples';
import WorkflowDiagram from '@/components/tutorial/WorkflowDiagram';
import TutorialScene from '@/components/tutorial/TutorialScene';
import {
  TUTORIAL_STEPS, BEST_PRACTICES, TUTORIAL_FAQ, TutorialAudience, bi, stepNarration,
} from '@/data/tutorial';

/**
 * /how-it-works — the public onboarding tutorial. `?for=gc` opens the General
 * Contractor version; default is Subcontractor. Linked from the onboarding
 * email, the dashboard Watch Demo modal and every embedded tutorial section.
 */
const HowItWorksPage: React.FC = () => {
  const { lang, path } = useI18n();
  const { profile } = useAuth();
  const [params, setParams] = useSearchParams();
  const audience: TutorialAudience = params.get('for') === 'gc' ? 'gc' : 'sub';
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const es = lang === 'es';

  const setAudience = (a: TutorialAudience) => {
    const next = new URLSearchParams(params);
    if (a === 'gc') next.set('for', 'gc');
    else next.delete('for');
    setParams(next, { replace: true });
  };

  const title = es ? 'Cómo funciona GIGGONE' : 'How GIGGONE Works';
  const desc = es
    ? 'Tutorial paso a paso para subcontratistas y contratistas generales: cuenta, verificación, leads, créditos, desbloqueo y cómo ganar trabajos.'
    : 'Step-by-step tutorial for subcontractors and general contractors: account, verification, leads, credits, unlocking and how to win jobs.';

  return (
    <div className="bg-slate-50">
      <Seo
        title={title}
        description={desc}
        schema={{
          '@type': 'HowTo',
          name: title,
          description: desc,
          step: TUTORIAL_STEPS.map((s, i) => ({
            '@type': 'HowToStep',
            position: i + 1,
            name: bi(s.title, lang),
            text: stepNarration(s, audience, lang),
          })),
        }}
      />

      {/* Hero */}
      <section className="bg-slate-900 pb-10 pt-14 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-black uppercase tracking-widest text-orange-400">{es ? 'Tutorial' : 'Tutorial'}</p>
          <h1 className="mt-2 text-4xl font-extrabold tracking-tight sm:text-5xl">{title}</h1>
          <p className="mt-4 max-w-2xl text-lg text-slate-300">{desc}</p>
          <div className="mt-6 inline-flex rounded-xl bg-white/10 p-1">
            {([
              ['sub', HardHat, es ? 'Para Subcontratistas' : 'For Subcontractors'],
              ['gc', Building2, es ? 'Para Contratistas Generales' : 'For General Contractors'],
            ] as const).map(([k, Icon, l]) => (
              <button
                key={k}
                type="button"
                onClick={() => setAudience(k)}
                className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-bold transition-colors ${
                  audience === k ? 'bg-white text-slate-900' : 'text-slate-300 hover:text-white'
                }`}
              >
                <Icon className="h-4 w-4" /> {l}
              </button>
            ))}
          </div>
          <TutorialPlayer defaultAudience={audience} lockAudience placement="how-it-works-page" className="mt-8" />
          <AnimatedExamples className="mt-14 rounded-3xl bg-slate-50 p-5 sm:p-8" />
        </div>
      </section>

      {/* Workflow */}
      <section className="py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-extrabold text-slate-900 sm:text-3xl">{es ? 'El flujo en 8 pasos' : 'The 8-step workflow'}</h2>
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <WorkflowDiagram />
          </div>
        </div>
      </section>

      {/* Detailed steps */}
      <section className="bg-white py-14">
        <div className="mx-auto max-w-7xl space-y-14 px-4 sm:px-6 lg:px-8">
          {TUTORIAL_STEPS.filter((s) => s.workflow).map((s, i) => (
            <div key={s.id} id={`step-${s.id}`} className={`grid scroll-mt-24 items-center gap-8 lg:grid-cols-2 ${i % 2 ? 'lg:[&>*:first-child]:order-2' : ''}`}>
              <div>
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-sm font-black text-white">{i + 1}</span>
                <h3 className="mt-3 text-2xl font-extrabold text-slate-900">{bi(s.title, lang)}</h3>
                <p className="mt-3 text-base leading-relaxed text-slate-600">{stepNarration(s, audience, lang)}</p>
                <p className="mt-4 inline-flex items-center gap-2 rounded-lg bg-orange-50 px-3 py-2 text-sm font-bold text-orange-800">
                  <MapPin className="h-4 w-4" /> {bi(s.where, lang)}
                </p>
              </div>
              <div className="flex justify-center rounded-3xl bg-gradient-to-br from-slate-900 to-slate-700 p-6 sm:p-10">
                <TutorialScene id={s.id} lang={lang} gc={audience === 'gc'} />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Best practices */}
      <section className="py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-extrabold text-slate-900 sm:text-3xl">{es ? 'Buenas prácticas para ganar trabajos' : 'Best practices for winning jobs'}</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {BEST_PRACTICES.map((b) => (
              <div key={b.title.en} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                <h3 className="mt-3 text-base font-extrabold text-slate-900">{bi(b.title, lang)}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{bi(b.body, lang)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-14">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-extrabold text-slate-900 sm:text-3xl">{es ? 'Preguntas frecuentes' : 'Common questions'}</h2>
          <div className="mt-6 divide-y divide-slate-200 overflow-hidden rounded-2xl border border-slate-200">
            {TUTORIAL_FAQ.map((f, i) => (
              <div key={f.q.en}>
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left hover:bg-slate-50"
                >
                  <span className="text-base font-bold text-slate-900">{bi(f.q, lang)}</span>
                  <ChevronDown className={`h-5 w-5 shrink-0 text-slate-400 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && <p className="px-5 pb-5 text-sm leading-relaxed text-slate-600">{bi(f.a, lang)}</p>}
              </div>
            ))}
          </div>
          <p className="mt-4 text-sm text-slate-500">
            {es ? '¿Aún tiene dudas?' : 'Still stuck?'}{' '}
            <Link to={path('/contact')} className="font-bold text-orange-600 underline">{es ? 'Contacte a soporte' : 'Contact support'}</Link>
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-14 pt-4">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center gap-5 rounded-3xl bg-gradient-to-br from-orange-500 to-orange-600 px-6 py-12 text-center">
            <h2 className="text-3xl font-extrabold text-white">{es ? '¿Listo para su primer lead?' : 'Ready for your first lead?'}</h2>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link
                to={profile ? path('/jobs') : `${path('/register')}?role=${audience === 'gc' ? 'gc' : 'subcontractor'}`}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-6 py-4 font-bold text-white hover:bg-slate-800"
              >
                {profile ? (es ? 'Ver proyectos' : 'Browse leads') : es ? 'Crear cuenta gratis' : 'Create free account'} <ArrowRight className="h-5 w-5" />
              </Link>
              <Link to={path('/pricing')} className="inline-flex items-center justify-center rounded-xl bg-white px-6 py-4 font-bold text-orange-600 hover:bg-orange-50">
                {es ? 'Ver precios' : 'See pricing'}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorksPage;
